import { useState, useRef, useCallback, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, useWindowDimensions } from 'react-native';
import { Howl } from 'howler';
import { useAppStore } from '../store/appStore';
import { buildAudioUrl } from '../services/utils';
import { Fonts, FaIcons } from '../styles/fonts';
import { useTheme } from '../styles/theme';
import { emitter } from '../services/events';
import { Tooltip } from './Tooltip';

const SEEK_PADDING = 5;
const THUMB_SIZE = 20;

interface PlaylistItem {
  ch: string | number;
  vs: string | number;
  url: string;
}

export default function RecitationPlayer() {
  const settings = useAppStore((s) => s.settings);
  const [controllerOpen, setControllerOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [seekPosition, setSeekPosition] = useState(0);
  const [repeatCount, setRepeatCount] = useState(1);
  const [seekbarWidth, setSeekbarWidth] = useState(0);
  const localRepeatRef = useRef(1);
  const [currentTrack, setCurrentTrack] = useState(0);
  const playlistRef = useRef<PlaylistItem[]>([]);
  const howlRef = useRef<Howl | null>(null);
  const rafRef = useRef<number>(0);
  const playingPlaylistRef = useRef(true);

  const broadcastState = useCallback((item: PlaylistItem | null, isPlaying: boolean) => {
    emitter.emit('gv-recitation-state', item
      ? { ch: item.ch, vs: item.vs, playing: isPlaying }
      : { playing: isPlaying },
    );
  }, []);

  const buildPlaylist = useCallback(() => {
    const items: PlaylistItem[] = [];
    const start = Number(settings.current_start_vs) || 1;
    const end = Number(settings.current_end_vs) || 20;
    const ch = settings.current_ch;
    for (let i = start; i <= end; i++) {
      items.push({ ch, vs: i, url: buildAudioUrl(ch, i, settings.current_reciter?.key) });
    }
    playlistRef.current = items;
    setCurrentTrack(0);
  }, [settings.current_ch, settings.current_start_vs, settings.current_end_vs, settings.current_reciter]);

  useEffect(() => { buildPlaylist(); }, [buildPlaylist]);

  const stopCurrentHowl = useCallback(() => {
    if (howlRef.current) {
      howlRef.current.stop();
      howlRef.current.unload();
      howlRef.current = null;
    }
    cancelAnimationFrame(rafRef.current);
    const item = playlistRef.current[currentTrack];
    broadcastState(item || null, false);
  }, [broadcastState, currentTrack]);

  const playTrackRef = useRef<(idx: number) => void>(() => {});

  const playTrack = useCallback((idx: number) => {
    stopCurrentHowl();
    const item = playlistRef.current[idx];
    if (!item) return;
    setCurrentTrack(idx);

    const howl = new Howl({
      src: [item.url],
      html5: true,
      preload: true,
      onplay: () => {
        setPlaying(true);
        broadcastState(item, true);
        const updateSeek = () => {
          if (howl.playing()) {
            const seek = howl.seek() as number;
            const duration = howl.duration();
            setSeekPosition(duration > 0 ? Math.ceil((seek * 100) / duration) : 0);
            rafRef.current = requestAnimationFrame(updateSeek);
          }
        };
        rafRef.current = requestAnimationFrame(updateSeek);
      },
      onend: () => {
        setPlaying(false);
        setSeekPosition(0);
        broadcastState(item, false);
        if (localRepeatRef.current < repeatCount) {
          localRepeatRef.current++;
          howl.play();
        } else if (playingPlaylistRef.current) {
          localRepeatRef.current = 1;
          const nextIdx = idx + 1;
          if (nextIdx < playlistRef.current.length) {
            playTrackRef.current(nextIdx);
          }
        }
      },
    });
    howlRef.current = howl;
    howl.play();
  }, [stopCurrentHowl, repeatCount, broadcastState]);

  useEffect(() => { playTrackRef.current = playTrack; }, [playTrack]);

  useEffect(() => {
    const handlePlayVerse = (detail: { ch: string | number; vs: string | number }) => {
      const { ch: evCh, vs: evVs } = detail;
      const idx = playlistRef.current.findIndex(
        (item) => String(item.ch) === String(evCh) && String(item.vs) === String(evVs)
      );
      if (idx >= 0) {
        playingPlaylistRef.current = false;
        playTrack(idx);
      } else {
        const url = buildAudioUrl(evCh, evVs, settings.current_reciter?.key);
        playlistRef.current = [{ ch: evCh, vs: evVs, url }];
        playingPlaylistRef.current = false;
        playTrack(0);
      }
    };
    emitter.on('gv-play-verse', handlePlayVerse);
    return () => { emitter.off('gv-play-verse', handlePlayVerse); };
  }, [playTrack]);

  const togglePlayPause = useCallback(() => {
    if (playing && howlRef.current) {
      howlRef.current.pause();
      setPlaying(false);
      broadcastState(playlistRef.current[currentTrack] || null, false);
    } else if (howlRef.current && !playing) {
      howlRef.current.play();
      setPlaying(true);
      playingPlaylistRef.current = true;
      broadcastState(playlistRef.current[currentTrack] || null, true);
    } else {
      playingPlaylistRef.current = true;
      playTrack(currentTrack);
    }
  }, [playing, currentTrack, playTrack, broadcastState]);

  const nextTrack = useCallback(() => {
    const next = currentTrack + 1;
    if (next < playlistRef.current.length) {
      playTrack(next);
      playingPlaylistRef.current = true;
    }
  }, [currentTrack, playTrack]);

  const previousTrack = useCallback(() => {
    const prev = currentTrack - 1;
    if (prev >= 0) {
      playTrack(prev);
      playingPlaylistRef.current = true;
    }
  }, [currentTrack, playTrack]);

  const increaseRepeat = useCallback(() => {
    setRepeatCount((prev) => (prev >= 5 ? 1 : prev + 1));
  }, []);

  const seekBackward3 = useCallback(() => {
    if (howlRef.current) {
      const current = howlRef.current.seek() as number;
      howlRef.current.seek(Math.max(0, current - 3));
    }
  }, []);

  const seekForward3 = useCallback(() => {
    if (howlRef.current) {
      const duration = howlRef.current.duration();
      const current = howlRef.current.seek() as number;
      howlRef.current.seek(Math.min(duration, current + 3));
    }
  }, []);

  const handleSeekPress = useCallback((e: any) => {
    if (!howlRef.current || seekbarWidth === 0) return;
    const trackLength = seekbarWidth - SEEK_PADDING * 2;
    const x = e.nativeEvent.locationX - SEEK_PADDING;
    const ratio = Math.max(0, Math.min(1, x / trackLength));
    howlRef.current.seek(howlRef.current.duration() * ratio);
  }, [seekbarWidth]);

  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width <= 768;
  const onVersePage = useAppStore((s) => s.onVersePage);

  if (!onVersePage) return null;

  // Seekbar geometry
  const trackLength = Math.max(0, seekbarWidth - SEEK_PADDING * 2);
  const fillWidth = (seekPosition / 100) * trackLength;
  const thumbLeft = SEEK_PADDING + fillWidth - THUMB_SIZE / 2;

  if (!controllerOpen) {
    return (
      <Pressable
        style={[styles.fab, { backgroundColor: colors.recitationBg }]}
        onPress={() => setControllerOpen(true)}
        nativeID="recitation-btn"
      >
        <Text style={[styles.fabIcon, { color: colors.recitationCtrlIcon }]}>{FaIcons.headphones}</Text>
      </Pressable>
    );
  }

  return (
    <View
      style={[styles.controller, isMobile ? styles.controllerMobile : styles.controllerDesktop]}
      nativeID="audio-recitation-container"
    >
      {/* Seekbar — straddles the top edge of the player box, renders above via zIndex */}
      <Pressable
        style={styles.seekRow}
        onLayout={(e) => setSeekbarWidth(e.nativeEvent.layout.width)}
        onPress={handleSeekPress}
      >
        <View style={[styles.seekTrack, { width: trackLength, backgroundColor: colors.seekTrackBg }]} />
        <View style={[styles.seekFill, { width: fillWidth, backgroundColor: colors.seekFillBg }]} />
        <View style={[styles.seekThumb, { left: thumbLeft, backgroundColor: colors.recitationCtrlIcon }]} />
      </Pressable>

      {/* Rounded player box — separate from controller so borderRadius isn't blocked by overflow:visible */}
      <View style={[styles.playerBox, { backgroundColor: colors.recitationBg }]}>
      {/* Controls */}
      <View style={styles.controlRow}>
        <Tooltip title="Repeat" placement="top">
          <Pressable onPress={increaseRepeat} style={styles.ctrlBtn}>
            <Text style={[styles.ctrlIcon, { color: colors.recitationCtrlIcon }]}>{FaIcons.retweet}</Text>
            {repeatCount > 1 && <Text style={[styles.repeatBadge, { backgroundColor: colors.repeatBadgeBg }]}>{repeatCount}</Text>}
          </Pressable>
        </Tooltip>
        <Tooltip title="Previous" placement="top">
          <Pressable onPress={previousTrack} style={styles.ctrlBtn}>
            <Text style={[styles.ctrlIcon, { color: colors.recitationCtrlIcon }]}>{FaIcons.backward}</Text>
          </Pressable>
        </Tooltip>
        <Tooltip title="3 sec backward" placement="top">
          <Pressable onPress={seekBackward3} style={styles.ctrlBtn}>
            <Text style={[styles.ctrlIcon, { color: colors.recitationCtrlIcon }]}>{FaIcons.stepBackward}</Text>
          </Pressable>
        </Tooltip>
        <Tooltip title="Play / Pause" placement="top">
          <Pressable onPress={togglePlayPause} style={[styles.ctrlBtn, styles.playBtn, { backgroundColor: colors.playBtnBg }]}>
          {playing ? <Text style={[styles.pauseIcon, { color: colors.recitationCtrlIcon }]}>{FaIcons.pause}</Text> : <Text style={[styles.playIcon, { color: colors.recitationCtrlIcon }]}>{FaIcons.play}</Text>}
          </Pressable>
        </Tooltip>
        <Tooltip title="3 sec forward" placement="top">
          <Pressable onPress={seekForward3} style={styles.ctrlBtn}>
            <Text style={[styles.ctrlIcon, { color: colors.recitationCtrlIcon }]}>{FaIcons.stepForward}</Text>
          </Pressable>
        </Tooltip>
        <Tooltip title="Next" placement="top">
          <Pressable onPress={nextTrack} style={styles.ctrlBtn}>
            <Text style={[styles.ctrlIcon, { color: colors.recitationCtrlIcon }]}>{FaIcons.forward}</Text>
          </Pressable>
        </Tooltip>
        <Tooltip title="Close" placement="top">
          <Pressable onPress={() => { stopCurrentHowl(); setControllerOpen(false); }} style={styles.ctrlBtn}>
            <Text style={[styles.ctrlIcon, { color: colors.recitationCtrlIcon }]}>{FaIcons.times}</Text>
          </Pressable>
        </Tooltip>
      </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  fab: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 900,
    elevation: 8,
  },
  fabIcon: {
    fontFamily: Fonts.FA,
    fontSize: 22,
  },
  controller: {
    position: 'absolute',
    zIndex: 900,
    elevation: 8,
    overflow: 'visible',
  },
  controllerDesktop: {
    bottom: 10,
    right: 10,
    width: 400,
  },
  controllerMobile: {
    bottom: 0,
    left: 0,
    right: 0,
  },
  playerBox: {
    borderRadius: 3,
    paddingBottom: 4,
    elevation: 8,
  },
  seekRow: {
    height: THUMB_SIZE,
    marginBottom: -(THUMB_SIZE / 2),
    zIndex: 2,
    elevation: 2,
  },
  seekTrack: {
    position: 'absolute',
    left: SEEK_PADDING,
    top: THUMB_SIZE / 2 - 1,
    height: 7,
    borderRadius: 2,
  },
  seekFill: {
    position: 'absolute',
    left: SEEK_PADDING,
    top: THUMB_SIZE / 2 - 1,
    height: 7,
    borderRadius: 2,
  },
  seekThumb: {
    position: 'absolute',
    top: 2.5,
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    elevation: 3,
  },
  controlRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: 10,
    paddingTop: 15,
  },
  ctrlBtn: {
    width: 40,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
  },
  ctrlIcon: {
    fontFamily: Fonts.FA,
    fontSize: 16,
  },
  playBtn: {
    width: 52,
    height: 52,
    borderRadius: 26,
  },
  playIcon: {
    fontFamily: Fonts.FA,
    fontSize: 22,
    marginLeft: 5,
  },
  pauseIcon: {
    fontFamily: Fonts.FA,
    fontSize: 22,
  },
  repeatBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    color: '#fff',
    fontSize: 10,
    width: 16,
    height: 16,
    borderRadius: 8,
    textAlign: 'center',
    lineHeight: 16,
  },
});
